import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Clock, CheckCircle2, Trophy, Medal } from 'lucide-react';
import { ClaimedLoan, LeaderboardEntry } from './types';
interface MyPipelineTilesProps {
  claims: ClaimedLoan[];
  me?: LeaderboardEntry;
  rank: number | null;
  totalPlayers: number;
}
export function MyPipelineTiles({
  claims,
  me,
  rank,
  totalPlayers
}: MyPipelineTilesProps) {
  const pending = claims.filter(c => c.status === 'pending').length;
  const funded = claims.filter(c => c.status === 'funded').length;
  const pendingVolume = claims.filter(c => c.status === 'pending').reduce((s, c) => s + c.amount, 0);
  const tiles = [{
    label: 'Total Claims',
    value: claims.length,
    sub: 'Your pipeline',
    icon: Briefcase,
    color: 'from-orange-500/20 to-red-600/20',
    iconColor: 'text-orange-400',
    borderColor: 'border-orange-500/30'
  }, {
    label: 'Pending Follow-Up',
    value: pending,
    sub: `$${(pendingVolume / 1000).toFixed(0)}k in play`,
    icon: Clock,
    color: 'from-yellow-500/20 to-orange-500/20',
    iconColor: 'text-yellow-400',
    borderColor: 'border-yellow-500/30'
  }, {
    label: 'Funded',
    value: funded,
    sub: 'Points earned',
    icon: CheckCircle2,
    color: 'from-emerald-500/20 to-green-600/20',
    iconColor: 'text-emerald-400',
    borderColor: 'border-emerald-500/30'
  }, {
    label: 'Your Points',
    value: (me?.points ?? 0).toLocaleString(),
    sub: 'This week',
    icon: Trophy,
    color: 'from-purple-500/20 to-pink-600/20',
    iconColor: 'text-purple-400',
    borderColor: 'border-purple-500/30'
  }, {
    label: 'Rank',
    value: rank ? `#${rank}` : '—',
    sub: rank ? `of ${totalPlayers}` : 'Unranked',
    icon: Medal,
    color: 'from-blue-500/20 to-indigo-600/20',
    iconColor: 'text-blue-400',
    borderColor: 'border-blue-500/30'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-id="element-416">
      {tiles.map((t, i) => <div key={i} className={`relative overflow-hidden rounded-2xl border ${t.borderColor} bg-gradient-to-br ${t.color} p-5 backdrop-blur-sm`} data-id="element-417">
          <div className="flex justify-between items-start mb-4" data-id="element-418">
            <div className="p-2 rounded-lg bg-slate-950/50" data-id="element-419">
              <t.icon className={`w-5 h-5 ${t.iconColor}`} data-id="element-420" />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-950/40 px-2 py-1 rounded-full" data-id="element-421">
              {t.sub}
            </span>
          </div>
          <h3 className="text-3xl font-bold font-display text-white mb-1" data-id="element-422">
            {t.value}
          </h3>
          <p className="text-xs font-medium text-slate-300" data-id="element-423">{t.label}</p>
          <div className="absolute -right-6 -bottom-6 opacity-10 pointer-events-none" data-id="element-424">
            <t.icon className="w-32 h-32" data-id="element-425" />
          </div>
        </div>)}
    </motion.div>;
}